import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    Grid,
    IconButton,
    Tooltip,
    Typography,
} from "@mui/material";
import { PageTitle } from "components/Classifiers/Common/PageTitle";
import AcxDataGrid from "components/UI/AcxDataGrid/AcxDataGrid";
import React, { useEffect } from "react";
import { useStore } from "utils/useStore";
import { LLMClassifiersStore } from "../Stores/LLMClassifiersStore";
import AcxButton from "components/UI/AcxButton";
import { Link, useNavigate } from "react-router-dom";
import { observer } from "mobx-react";
import { AuthStore } from "stores/AuthStore";
import AcxMainTextField from "components/UI/AcxMainTextField";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { ContentCopy, Delete } from "@mui/icons-material";
import { AcxConfirmDeleteDialog } from "components/UI/AcxConfirmDeleteDialog";
import _ from "lodash";
import { UnpublishButton } from "components/Classifiers/Common/UnpublishButton";
import { LLMClassifierLibraryStore } from "../Stores/LLMClassifierLibraryStore";
import { LLMCLassifierDetailsResponse } from "models/LLMs";
import { ModelPublishDialog } from "../Components/ModelPublishDialog";
import { RunOnHistoryButton } from "../Components/RunOnHistoryButton";
import ClassifierBuilderV2ReclassifyDialog from "components/Classifiers/ClassifierBuilderV2/Views/Dialogs/ClassifierBuilderV2ReclassifyDialog";
import ClassifierBuilderV2ReclassifyDialogStore from "components/Classifiers/ClassifierBuilderV2/Stores/ClassifierBuilderV2ReclassifyDialogStore";
import theme from "Theme/AppTheme";

export const YourModels = observer(function () {
    const llmClassifiersStore = useStore(LLMClassifiersStore);
    const llmClassifierLibraryStore = useStore(LLMClassifierLibraryStore);
    const authStore = useStore(AuthStore);
    const reclassifyStore = useStore(ClassifierBuilderV2ReclassifyDialogStore);

    const navigate = useNavigate();

    const [isDeleteOpen, setIsDeleteOpen] = React.useState(false);
    const [isUnarchiveToPublishedOpen, setIsUnarchiveToPublishedOpen] =
        React.useState(false);
    const [isModelPublishDialogOpen, setIsModelPublishDialogOpen] =
        React.useState(false);
    const [modelPublishDialogInfo, setModelPublishDialogInfo] = React.useState<{
        dashboardTitle?: string;
        dashboardId: string;
    }>({ dashboardId: "" });

    useEffect(() => {
        llmClassifiersStore.loadLLMClassifiers();
        return () => llmClassifiersStore.cancelLoadLLMClassifiers();
    }, [llmClassifiersStore]);

    // The dialog store's org is otherwise only set by the Lucene pages, so on a cold load of /classifiers/llm Reclassify would stay disabled
    useEffect(() => {
        if (authStore.orgStore.selectedOrganization) {
            reclassifyStore.orgSelectStore.selectOrg(
                authStore.orgStore.selectedOrganization,
            );
        }
    }, [authStore.orgStore.selectedOrganization, reclassifyStore]);

    const isPublishLoading = llmClassifiersStore.getTaskLoading(
        LLMClassifiersStore.Tasks.PublishLLMClassifier,
    );
    const isUnpublishLoading = llmClassifiersStore.getTaskLoading(
        LLMClassifiersStore.Tasks.UnpublishLLMClassifier,
    );
    const isCopyLoading = llmClassifiersStore.getTaskLoading(
        LLMClassifiersStore.Tasks.CopyLLMClassifier,
    );
    const isAddToLibraryLoading = llmClassifierLibraryStore.getTaskLoading(
        LLMClassifierLibraryStore.Tasks.AddToLibrary,
    );
    const isRemoveFromLibraryLoading = llmClassifierLibraryStore.getTaskLoading(
        LLMClassifierLibraryStore.Tasks.RemoveFromLibrary,
    );
    const isArchiveLoading = llmClassifiersStore.getTaskLoading(
        LLMClassifiersStore.Tasks.ArchiveLLMClassifier,
    );
    const isUnarchiveLoading = llmClassifiersStore.getTaskLoading(
        LLMClassifiersStore.Tasks.UnarchiveLLMClassifier,
    );
    const isAnyLoading =
        isPublishLoading ||
        isUnpublishLoading ||
        isCopyLoading ||
        isAddToLibraryLoading ||
        isRemoveFromLibraryLoading ||
        isArchiveLoading ||
        isUnarchiveLoading;

    function handleUnarchiveClick(classifier: LLMCLassifierDetailsResponse) {
        if (classifier.isActive) return;

        if (classifier.isPublished) {
            setIsUnarchiveToPublishedOpen(true);
            return;
        }

        handleConfirmUnarchive(classifier.id);
    }

    async function handleConfirmUnarchive(id: string) {
        await llmClassifiersStore.unarchiveLLMClassifier(id);
        setIsUnarchiveToPublishedOpen(false);
    }

    const publishedLlmClassifiers = llmClassifiersStore.llmClassifiers.filter(
        (c) => c.isPublished,
    );

    // Same gate as the Lucene table's Reclassify button
    const restrictReclassify =
        !authStore.isUserUltra() &&
        authStore.canUserView("Restrict Reclassify");

    return (
        <Grid
            container
            sx={(theme) => ({
                height: "100%",
                padding: theme.spacing(5, 10, 10, 10),
            })}
        >
            <Grid container alignItems="center" justifyContent="space-between">
                <Grid item>
                    <PageTitle>Your Models</PageTitle>
                </Grid>
                {/* The history route is only registered for View "Builder" */}
                {authStore.canUserView("Builder") && (
                    <Grid item>
                        <Link
                            className="pendo-ignore"
                            to="../reclassify-history?tab=model-builder"
                            relative="path"
                            style={{ color: theme.palette.text.primary }}
                        >
                            View Reclassify History
                        </Link>
                    </Grid>
                )}
            </Grid>
            <AcxDataGrid dataGridStore={llmClassifiersStore.dataGridStore}>
                <Grid
                    sx={{
                        position: "absolute",
                        left: "0px",
                        marginRight: "auto",
                    }}
                >
                    <AcxMainTextField
                        id="llm-classifier-search"
                        placeholderText="Search models..."
                        value={llmClassifiersStore.searchString}
                        onChange={(event) => {
                            const search = event.target.value;
                            llmClassifiersStore.searchLLMClassifiers(search);
                        }}
                        startAdornment={
                            <SearchIcon style={{ marginRight: "0.5rem" }} />
                        }
                        endAdornment={
                            llmClassifiersStore.searchString && (
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        llmClassifiersStore.searchLLMClassifiers();
                                    }}
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            )
                        }
                    />
                </Grid>
                {authStore.canUserEdit("Model Builder") && (
                    <>
                        {authStore.isUserUltra() &&
                            authStore.orgStore.isCurrentOrganizationAuthcx() && (
                                <>
                                    <AcxButton
                                        color="white"
                                        disabled={
                                            !llmClassifiersStore.singleSelectedLLMClassifier ||
                                            !llmClassifiersStore
                                                .singleSelectedLLMClassifier
                                                .isPublished ||
                                            isRemoveFromLibraryLoading ||
                                            LLMClassifierLibraryStore.doesClassifierBelongToLibrary(
                                                llmClassifiersStore.singleSelectedLLMClassifier,
                                            )
                                        }
                                        loading={isAddToLibraryLoading}
                                        onClick={() =>
                                            llmClassifierLibraryStore.addToLibrary(
                                                llmClassifiersStore.singleSelectedLLMClassifier!,
                                            )
                                        }
                                    >
                                        Add to Library
                                    </AcxButton>
                                    <AcxButton
                                        color="white"
                                        disabled={
                                            !llmClassifiersStore.singleSelectedLLMClassifier ||
                                            !llmClassifiersStore
                                                .singleSelectedLLMClassifier
                                                .isPublished ||
                                            isAddToLibraryLoading ||
                                            !LLMClassifierLibraryStore.doesClassifierBelongToLibrary(
                                                llmClassifiersStore.singleSelectedLLMClassifier,
                                            )
                                        }
                                        loading={isRemoveFromLibraryLoading}
                                        onClick={() =>
                                            llmClassifierLibraryStore.removeFromLibrary(
                                                llmClassifiersStore.singleSelectedLLMClassifier!,
                                            )
                                        }
                                    >
                                        Remove from Library
                                    </AcxButton>
                                </>
                            )}
                        <AcxButton
                            color="white"
                            containerStyle={{ width: "28px", height: "28px" }}
                            disabled={
                                isAnyLoading ||
                                !llmClassifiersStore.singleSelectedLLMClassifier
                            }
                            loading={isCopyLoading}
                            sx={(theme) => ({
                                width: "28px",
                                height: "28px",
                                minWidth: "0px",
                                fontSize: "16px",
                                padding: theme.spacing(1),
                            })}
                            onClick={async () => {
                                await llmClassifiersStore.copyLLMClassifier(
                                    llmClassifiersStore.singleSelectedLLMClassifier!
                                        .id,
                                );
                                await llmClassifiersStore.loadLLMClassifiers();
                            }}
                        >
                            <ContentCopy fontSize="inherit" />
                        </AcxButton>
                        <AcxButton
                            color="white"
                            containerStyle={{ width: "28px", height: "28px" }}
                            disabled={
                                !llmClassifiersStore.singleSelectedLLMClassifier ||
                                isAnyLoading
                            }
                            sx={(theme) => ({
                                width: "28px",
                                height: "28px",
                                minWidth: "0px",
                                fontSize: "16px",
                                padding: theme.spacing(1),
                            })}
                            onClick={() => setIsDeleteOpen(true)}
                        >
                            <Delete fontSize="inherit" />
                        </AcxButton>
                        {authStore.isUserUltra() && (
                            <RunOnHistoryButton
                                classifiers={publishedLlmClassifiers}
                                selectedClassifier={
                                    llmClassifiersStore.singleSelectedLLMClassifier
                                }
                                disabled={
                                    isAnyLoading ||
                                    llmClassifiersStore.dataGridStore
                                        .selectedRows.length > 1 ||
                                    (!!llmClassifiersStore.singleSelectedLLMClassifier &&
                                        !llmClassifiersStore
                                            .singleSelectedLLMClassifier
                                            .isPublished)
                                }
                            />
                        )}
                        {authStore.canUserEdit("Reclassify") && (
                            <AcxButton
                                color="white"
                                disabled={
                                    isAnyLoading ||
                                    restrictReclassify ||
                                    !reclassifyStore.orgSelectStore.orgId ||
                                    llmClassifiersStore
                                        .selectedReclassifiableLLMClassifiers
                                        .length === 0
                                }
                                disabledTooltip={
                                    restrictReclassify
                                        ? "This role does not have access to reclassify."
                                        : "Select one or more published, unarchived models to reclassify."
                                }
                                onClick={() => {
                                    reclassifyStore.setClassifierType("LLM");
                                    reclassifyStore.setSelectedClassifiers(
                                        llmClassifiersStore.selectedReclassifiableLLMClassifiers,
                                    );
                                    reclassifyStore.dialog.open();
                                }}
                            >
                                Reclassify
                            </AcxButton>
                        )}
                        {llmClassifiersStore.singleSelectedLLMClassifier && (
                            <AcxConfirmDeleteDialog
                                isOpen={isDeleteOpen}
                                onOpenChange={setIsDeleteOpen}
                                onDelete={async () => {
                                    await llmClassifiersStore.deleteLLMClassifier(
                                        llmClassifiersStore.singleSelectedLLMClassifier!
                                            .id,
                                    );
                                }}
                                requiredText={
                                    llmClassifiersStore.singleSelectedLLMClassifier!
                                        .name
                                }
                            >
                                {llmClassifiersStore.singleSelectedLLMClassifier
                                    .smartPredictConfiguration.length > 0 && (
                                    <Grid
                                        container
                                        item
                                        direction="column"
                                        flexWrap="nowrap"
                                        pb={1}
                                        rowGap={0.25}
                                    >
                                        <Grid item pb={0.5}>
                                            <Typography fontWeight="600">
                                                Deleting this model will
                                                permanently delete all results
                                                history. This model is being
                                                used to SmartPredict answers on
                                                the following module questions:
                                            </Typography>
                                        </Grid>
                                        {_.uniqBy(
                                            llmClassifiersStore
                                                .singleSelectedLLMClassifier
                                                .smartPredictConfiguration,
                                            (c) => c.questionId,
                                        ).map((c) => {
                                            const text = `${c.module} - ${c.question}`;

                                            if (
                                                authStore.canUserEdit("Modules")
                                            ) {
                                                return (
                                                    <Grid
                                                        item
                                                        zeroMinWidth
                                                        sx={{
                                                            overflow: "hidden",
                                                            textOverflow:
                                                                "ellipsis",
                                                        }}
                                                    >
                                                        <Tooltip
                                                            title={text}
                                                            PopperProps={{
                                                                placement:
                                                                    "bottom-start",
                                                            }}
                                                        >
                                                            <Link
                                                                className="pendo-ignore"
                                                                to={`/focus/modules/${authStore.orgStore.selectedOrganization?.id}/${c.moduleId}`}
                                                                target="_blank"
                                                                style={{
                                                                    textWrap:
                                                                        "nowrap",
                                                                    whiteSpace:
                                                                        "nowrap",
                                                                }}
                                                            >
                                                                {text}
                                                            </Link>
                                                        </Tooltip>
                                                    </Grid>
                                                );
                                            }

                                            return (
                                                <Tooltip title={text}>
                                                    <Grid item>
                                                        <Typography
                                                            sx={{
                                                                overflow:
                                                                    "hidden",
                                                                textOverflow:
                                                                    "ellipsis",
                                                                textWrap:
                                                                    "nowrap",
                                                            }}
                                                        >
                                                            {text}
                                                        </Typography>
                                                    </Grid>
                                                </Tooltip>
                                            );
                                        })}
                                    </Grid>
                                )}
                            </AcxConfirmDeleteDialog>
                        )}
                        <AcxButton
                            disabled={
                                !llmClassifiersStore.singleSelectedLLMClassifier ||
                                !llmClassifiersStore.singleSelectedLLMClassifier
                                    .isActive ||
                                isAnyLoading
                            }
                            onClick={async () => {
                                await llmClassifiersStore.archiveLLMClassifier(
                                    llmClassifiersStore.singleSelectedLLMClassifier!
                                        .id,
                                );
                            }}
                            loading={isArchiveLoading}
                            disabledTooltip={
                                !llmClassifiersStore.singleSelectedLLMClassifier
                                    ? "No model selected"
                                    : "This model is already archived"
                            }
                            color="white"
                        >
                            Archive
                        </AcxButton>
                        <AcxButton
                            disabled={
                                !llmClassifiersStore.singleSelectedLLMClassifier ||
                                llmClassifiersStore.singleSelectedLLMClassifier
                                    .isActive ||
                                isAnyLoading
                            }
                            onClick={async () => {
                                handleUnarchiveClick(
                                    llmClassifiersStore.singleSelectedLLMClassifier!,
                                );
                            }}
                            loading={isUnarchiveLoading}
                            color="white"
                        >
                            Unarchive
                        </AcxButton>
                        {llmClassifiersStore.singleSelectedLLMClassifier && (
                            <Dialog
                                open={isUnarchiveToPublishedOpen}
                                onClose={() =>
                                    setIsUnarchiveToPublishedOpen(false)
                                }
                                maxWidth="xs"
                                fullWidth
                            >
                                <DialogContent>
                                    <Grid
                                        container
                                        direction="column"
                                        rowGap={2}
                                    >
                                        <Typography
                                            sx={(theme) => ({
                                                fontWeight: 600,
                                                fontSize: "20px",
                                                color: theme.palette.gray[700],
                                                lineHeight: "32px",
                                            })}
                                        >
                                            Unarchive Published Model
                                        </Typography>
                                        <DialogContentText>
                                            This model was previously published
                                            and archived.
                                        </DialogContentText>
                                        <DialogContentText>
                                            Unarchiving it will automatically
                                            return it to a published state and
                                            resume its active status in the
                                            platform.
                                        </DialogContentText>
                                    </Grid>
                                </DialogContent>
                                <DialogActions>
                                    <AcxButton
                                        color="white"
                                        onClick={() =>
                                            setIsUnarchiveToPublishedOpen(false)
                                        }
                                        disabled={isUnarchiveLoading}
                                    >
                                        Cancel
                                    </AcxButton>
                                    <AcxButton
                                        onClick={() =>
                                            handleConfirmUnarchive(
                                                llmClassifiersStore.singleSelectedLLMClassifier!
                                                    .id,
                                            )
                                        }
                                        loading={isUnarchiveLoading}
                                    >
                                        Unarchive
                                    </AcxButton>
                                </DialogActions>
                            </Dialog>
                        )}
                        <UnpublishButton
                            disabled={
                                isAnyLoading ||
                                !llmClassifiersStore.singleSelectedLLMClassifier ||
                                !llmClassifiersStore.singleSelectedLLMClassifier
                                    .isPublished
                            }
                            onConfirmUnpublish={async () => {
                                await llmClassifiersStore.unpublishLLMClassifier(
                                    llmClassifiersStore.singleSelectedLLMClassifier!
                                        .id,
                                );
                            }}
                            classifierWording="model"
                        />
                        <Tooltip
                            title={
                                llmClassifiersStore.singleSelectedLLMClassifier
                                    ?.referencesLlmModel
                                    ? "This model can't be published because it references another LLM model. Open it in Model Builder to see which one."
                                    : ""
                            }
                        >
                            <span>
                                <AcxButton
                                    color="white"
                                    disabled={
                                        isAnyLoading ||
                                        !llmClassifiersStore.singleSelectedLLMClassifier ||
                                        llmClassifiersStore
                                            .singleSelectedLLMClassifier
                                            .isPublished ||
                                        llmClassifiersStore
                                            .singleSelectedLLMClassifier
                                            .referencesLlmModel
                                    }
                                    loading={isPublishLoading}
                                    onClick={async () => {
                                        await llmClassifiersStore.publishLLMClassifier(
                                            llmClassifiersStore.singleSelectedLLMClassifier!
                                                .id,
                                            llmClassifiersStore.singleSelectedLLMClassifier!
                                                .displayedImplementationId,
                                            (result) => {
                                                if (!result.dashboardId) return;
                                                setModelPublishDialogInfo({
                                                    dashboardId:
                                                        result.dashboardId,
                                                    dashboardTitle:
                                                        result.dashboardTitle ??
                                                        undefined,
                                                });
                                                setIsModelPublishDialogOpen(
                                                    true,
                                                );
                                            },
                                        );
                                    }}
                                >
                                    Publish
                                </AcxButton>
                            </span>
                        </Tooltip>
                        <AcxButton
                            color="white"
                            disabled={
                                isAnyLoading ||
                                !llmClassifiersStore.singleSelectedLLMClassifier
                            }
                            onClick={async () => {
                                navigate(
                                    `./builder/${
                                        llmClassifiersStore.singleSelectedLLMClassifier!
                                            .id
                                    }`,
                                );
                            }}
                        >
                            Edit
                        </AcxButton>
                        <Link
                            className="pendo-ignore"
                            to="./builder/new"
                            style={{ textDecoration: "none" }}
                        >
                            <AcxButton>+ New Model</AcxButton>
                        </Link>
                    </>
                )}
            </AcxDataGrid>
            {authStore.isUserUltra() && (
                <ModelPublishDialog
                    isOpen={isModelPublishDialogOpen}
                    setIsOpen={setIsModelPublishDialogOpen}
                    modelPublishDialogInfo={modelPublishDialogInfo}
                />
            )}
            <ClassifierBuilderV2ReclassifyDialog
                defaultValue={
                    llmClassifiersStore.selectedReclassifiableLLMClassifiers
                }
                classifierOptions={
                    llmClassifiersStore.reclassifiableLLMClassifiers
                }
            />
        </Grid>
    );
});
